import logger from '@eb/logger-bugsnag';

import { Collection } from '../../types';

import {
    getCollectionsForEvent,
    saveEvent as saveEventApi,
    unSaveEvent,
    addToCollection,
    removeFromCollection,
    createCollection,
} from './api';
import { loggedOutSaveRedirect } from './utils';

/**
 *
 * @param object
 *
 * Keys:
 *
 * `eventId`: the event to save
 *
 * `isAuthenticated`: if user is logged in
 *
 * `savedByYou`: the current save status of the event
 *
 * returns `{id, savedByYou}`
 */
export const saveEvent = async ({
    eventId,
    isAuthenticated,
    savedByYou,
}: {
    eventId: string;
    isAuthenticated: boolean;
    savedByYou: boolean;
}): Promise<{ id?: string; newSaveState?: boolean } | undefined> => {
    try {
        if (!isAuthenticated) {
            loggedOutSaveRedirect(eventId);
            return;
        }

        const apiAction = savedByYou ? unSaveEvent : saveEventApi;
        await apiAction(eventId);

        return { id: eventId, newSaveState: !savedByYou };
    } catch (error) {
        // Sometimes the backend provides events that are incorreclty
        // marked as not saved. This addresses that by simply swallowing
        // the error and allow the local state to be updated hoping that
        // the next reload will behave properly.
        if (error?.parsedError?.error === 'BOOKMARK_ALREADY_EXISTS') {
            logger.warn('Bookmark already exists:', {
                error: error.message ? error.message : error,
            });

            return { id: eventId, newSaveState: !savedByYou };
        }

        throw error;
    }
};

/**
 *
 * @param object
 *
 * keys:
 *
 * `eventId`: Current event
 *
 * `userCollections`: The current logged in user's collections
 *
 * returns `{id, collections}` where `collections` is an array of collection
 * ids representing the union of the user's collections and collections
 * the event is a member of.
 */
export const handleShowAddToCollectionView = async ({
    eventId,
    userCollections = [],
}: {
    eventId: string;
    userCollections: Collection[];
}) => {
    if (userCollections.length > 0) {
        try {
            const response = await getCollectionsForEvent({ id: eventId });
            const collections = response?.collections || [];
            const collectionIds = collections.map(
                (collection) => collection.id,
            );

            return { id: eventId, collections: collectionIds };
        } catch (error) {
            logger.error('Error fetching collections for event:', {
                error: error.message ? error.message : error,
            });
        }
    }
};

export const handleCreateCollection = async ({
    eventId,
    name,
}: {
    eventId: string;
    name: string;
}) => {
    try {
        const createdCollection = await createCollection(name, eventId);
        return createdCollection;
    } catch (error) {
        logger.error('Error creating collection:', {
            error: error.message ? error.message : error,
        });
        throw error;
    }
};

export const handleAddOrRemoveToCollection = async ({
    eventId,
    collectionId,
    isChecked,
}: {
    eventId: string;
    collectionId: string;
    isChecked: boolean;
}): Promise<{ collectionId: string }> => {
    const action = isChecked ? addToCollection : removeFromCollection;

    try {
        const response = await action(collectionId, eventId);
        return { collectionId };
    } catch (error) {
        logger.error('Error updating collection membership:', {
            error: error.message ? error.message : error,
        });
        throw error;
    }
};
